import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import type {BentoGridSliceIconCardsPrimaryCardsItem} from 'prismicio-types'
import type {FC} from 'react'
import {memo} from 'react'

type IconCardProps = {
  card: BentoGridSliceIconCardsPrimaryCardsItem
  isDark?: boolean
}

/**
 * IconCard - A simplified card component for the BentoGrid iconCards variation
 *
 * Features:
 * - Icon with gradient background (32x32px mobile, 48x48px desktop)
 * - Title (20px mobile, H4 desktop)
 * - Content/description text
 * - Mobile: Icon and title side-by-side
 * - Desktop: Icon stacked above title
 * - Memoized to prevent unnecessary re-renders in lists
 */
const IconCardComponent: FC<IconCardProps> = ({card, isDark = false}) => (
  <div
    className={cn('relative flex flex-col gap-4 rounded-lg p-6 md:gap-4', isDark ? 'bg-green-light' : 'bg-moss-white')}
  >
    {/* Icon & Title - Side by side on mobile, stacked on desktop */}
    <div className="flex items-center gap-4 md:flex-col md:items-start md:gap-6">
      {/* Icon */}
      {isFilled.image(card.icon) && (
        <div className="flex size-8 shrink-0 items-center justify-center rounded bg-[linear-gradient(321deg,#AF57F7_24.18%,#D9ABFF_100.4%)] p-1.5 md:size-12 md:p-2">
          <div
            className="size-full bg-moss-white"
            style={{
              maskImage: `url(${card.icon.url})`,
              maskSize: 'contain',
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
              WebkitMaskImage: `url(${card.icon.url})`,
              WebkitMaskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
            }}
          />
        </div>
      )}

      {/* Title */}
      {isFilled.richText(card.title) && (
        <PrismicRichText
          field={card.title}
          components={{
            heading3: ({children}) => (
              <h3
                className={cn(
                  'md:text-h4 text-20 font-medium leading-6 tracking-[0.2px] md:leading-8 md:tracking-normal',
                  isDark ? 'text-moss-white' : 'text-moss-black',
                )}
              >
                {children}
              </h3>
            ),
          }}
        />
      )}
    </div>

    {/* Description */}
    {isFilled.richText(card.content) && (
      <PrismicRichText
        field={card.content}
        components={{
          paragraph: ({children}) => (
            <p
              className={cn(
                'md:text-tg-body-l text-16 leading-[22px]',
                isDark ? 'text-moss-white' : 'text-moss-grey-400',
              )}
            >
              {children}
            </p>
          ),
        }}
      />
    )}
    {/* Decorative inner border */}
    <div className="pointer-events-none absolute inset-0 rounded-lg border border-moss-black/10" />
  </div>
)

IconCardComponent.displayName = 'IconCard'

export const IconCard = memo(IconCardComponent)
